'use client';

import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useFirebaseService } from '@/hooks/useFirebaseService';
import { useAuth } from '@/contexts/authContext';
import UserInfo2 from '@/functions/src/entities/userInfo2';
import ListInfo from '@/functions/src/entities/listInfo';
import SummaryDetailInfo from '@/functions/src/entities/summaryDetailInfo';
import BatchRunInfo from '@/functions/src/entities/batchRunInfo';
import { Zap, TrendingUp, BarChart3, List } from 'lucide-react';

// V2 Components
import {
  MarketBriefingSection,
  BriefingPeriod
} from '@/components/home-v2';

// V2 Components
import { HeaderV2 } from '@/components/home-v2';

// Dynamically import components that might use browser APIs
const Footer = dynamic(() => import('@/components/footer'), { ssr: false });
const NewsLetterContentWrapper = dynamic(() => import('@/components/newsletter-content-wrapper'), { ssr: false });
const HeadlineComponent = dynamic(() => import('@/components/headline-component'), { ssr: false });
const SubscriptionStart = dynamic(() => import('@/components/subscription-start'), { ssr: false });
const SentimentScoreDots = dynamic(() => import('@/components/sentiment-score-dots').then(mod => ({ default: mod.SentimentScoreDots })), { ssr: false });
const FinancialBulletin = dynamic(() => import('@/components/financial-bulletin'), { ssr: false });
import { PageLoadingSkeleton } from '@/components/skeletons/home-skeletons';

// Hidden element to trigger camera functionality from welcome dialog
const CameraTrigger = () => (
  <span id="camera-icon-trigger" className="hidden"></span>
);

export default function Home() {
  const SYSTEM_USER_ID = 'SYSTEM_USER_ID';
  const [loading, setLoading] = useState(true);
  const { getListInfosForUser, getLatestBatchId, getLatestPrimaryBatchId, getUserById, getBatchRunInfo } = useFirebaseService();
  const [batchId, setBatchId] = useState<string>('');
  const [systemUserBatchId, setSystemUserBatchId] = useState<string>('');
  const [batchRunInfo, setBatchRunInfo] = useState<BatchRunInfo | null>(null);
  const [sentimentMode, setSentimentMode] = useState<'market' | 'personalized'>('market');
  const [showSubscriptionStart, setShowSubscriptionStart] = useState(false);
  const { userInfo, isAuthStateChanging } = useAuth();
  const [user, setUser] = useState<UserInfo2 | null>(null);
  const [finalListIds, setFinalListIds] = useState<{ id: string, caption: string }[]>([]);
  const [summaryDetailInfo, setSummaryDetailInfo] = useState<SummaryDetailInfo | null>(null);
  const [summaryDetailInfoList, setSummaryDetailInfoList] = useState<SummaryDetailInfo[]>([]);
  const [headlineExpanded, setHeadlineExpanded] = useState(false);
  const [lists, setLists] = useState<ListInfo[]>([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [isUserCheckInProgress, setIsUserCheckInProgress] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [showListView, setShowListView] = useState(false);
  const [showSubscribeTag, setShowSubscribeTag] = useState(true);

  const [hasSentimentData, setHasSentimentData] = useState(false);
  const [hasBulletinData, setHasBulletinData] = useState(false);
  const [audioUrls, setAudioUrls] = useState<{ audioUrl1?: string; audioUrl2?: string; audioUrl3?: string }>({});

  const [activePeriod, setActivePeriod] = useState<BriefingPeriod>('morning');

  const standardListIds = [
    { id: 'TOP_SP500', caption: 'TOP SP500' },
    { id: 'TOP_DJI', caption: 'TOP DJI' },
    { id: 'TOP_LEADERS', caption: 'TOP LEADERS' },
    { id: 'TOP_LOSERS', caption: 'TOP LOSERS' }
  ];

  useEffect(() => {
    setShowSubscriptionStart(false);
    setIsUserCheckInProgress(true);
  }, []);

  useEffect(() => {
    if (isAuthStateChanging) return;

    const currUser: UserInfo2 | null = userInfo;

    if (!currUser) {
      const fetchSystemUser = async () => {
        try {
          const systemUser = await getUserById(SYSTEM_USER_ID);
          if (systemUser) {
            if (!userInfo) {
              if (user?.uid !== systemUser.uid) {
                setUser(systemUser);
              }
              const sysUserBatchId = await getLatestBatchId(SYSTEM_USER_ID);
              if (sysUserBatchId) {
                setBatchId(sysUserBatchId);
                setSystemUserBatchId(sysUserBatchId);
                const batchInfo = await getBatchRunInfo(sysUserBatchId);
                setBatchRunInfo(batchInfo);
              }
              // Extract audio URLs from system user
              setAudioUrls({
                audioUrl1: systemUser.audioUrl1,
                audioUrl2: systemUser.audioUrl2,
                audioUrl3: systemUser.audioUrl3
              });
              setSentimentMode('market');
            }
          } else {
            if (user !== null) setUser(null);
            console.error('SYSTEM_USER not found in database');
          }
        } catch (error) {
          console.error('Error fetching SYSTEM_USER:', error);
          if (user !== null) setUser(null);
        }
      };

      setFinalListIds(standardListIds);
      fetchSystemUser();
      setIsUserCheckInProgress(false);
      return;
    }

    if (user?.uid !== currUser.uid) {
      setUser(currUser);
    }

    const fetchUserData = async (user: UserInfo2) => {
      try {
        const [userBatchId, sysUserBatchId] = await Promise.all([
          getLatestBatchId(user.uid),
          getLatestBatchId(SYSTEM_USER_ID)
        ]);
        if (userBatchId) {
          setBatchId(userBatchId);
        }
        if (sysUserBatchId) {
          setSystemUserBatchId(sysUserBatchId);
          const batchInfo = await getBatchRunInfo(sysUserBatchId);
          setBatchRunInfo(batchInfo);
        }
        // Extract audio URLs from logged-in user
        setAudioUrls({
          audioUrl1: user.audioUrl1,
          audioUrl2: user.audioUrl2,
          audioUrl3: user.audioUrl3
        });
        setSentimentMode('personalized');
      } catch (error) {
        console.error('Error fetching batch ID:', error);
      }
      try {
        setLoadingLists(true);
        const userLists = await getListInfosForUser(user.uid);

        if (JSON.stringify(lists) !== JSON.stringify(userLists)) {
          setLists(userLists);
        }

        if (userLists.length > 0) {
          const userListsFormatted = userLists.map((list: ListInfo) => ({
            id: list.id,
            caption: list.name
          }));
          setFinalListIds(userListsFormatted);
        } else {
          setFinalListIds(standardListIds);
        }

        const createdOn = user.createdOn ? new Date(user.createdOn) : new Date();
        const summary: SummaryDetailInfo = {
          summaryHeader: user.summaryHeader || '',
          shortSummary: user.shortSummary || '',
          summaryFull: user.summaryFull || [],
          summaryReadTime: user.summaryReadTime || 0,
          createdOn: createdOn,
          createdOnL: createdOn.getTime() || 0,
        };

        setSummaryDetailInfo(summary);

      } catch (err: any) {
        console.error('Error processing user data:', err);
        setError(err as Error);
        setFinalListIds(standardListIds);
      } finally {
        setLoadingLists(false);
        setIsUserCheckInProgress(false);
      }
    };

    fetchUserData(currUser);
  }, [isAuthStateChanging, userInfo]);

  useEffect(() => {
    setLoading(false);
  }, []);

  return (
    <div className="min-h-screen text-[#e6edf3] font-body bg-[#0a0e12]">
      {/* Background Effects - Refined Atmospheric Depth */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {/* Subtle Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(251, 191, 36, 0.4) 0.5px, transparent 0.5px),
                               linear-gradient(90deg, rgba(251, 191, 36, 0.4) 0.5px, transparent 0.5px)`,
              backgroundSize: '48px 48px'
            }}
          />
        </div>
        {/* Atmospheric Gradient Layers - Very Subtle */}
        <div className="absolute top-0 left-1/3 w-[800px] h-[800px] bg-gradient-radial from-amber-500/[0.03] via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-[600px] h-[600px] bg-gradient-radial from-blue-500/[0.02] via-transparent to-transparent rounded-full blur-3xl" />
        {/* Noise Texture Overlay */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat',
            backgroundSize: '128px 128px'
          }}
        />
      </div>

      {/* Header */}
      <HeaderV2 />
      <CameraTrigger />

      {/* Main Content */}
      <main className="relative z-10 flex-grow">
        {/* Subscription Dialog */}
        {!isUserCheckInProgress && showSubscriptionStart && (
          <div className="subscription-container fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity duration-300">
            <SubscriptionStart
              isOpen={true}
              onClose={() => setShowSubscriptionStart(false)}
              onGetFreeEdition={() => setShowSubscriptionStart(false)}
              hasWatchlists={lists.length > 0}
            />
          </div>
        )}

        {/* Loading State */}
        {(isAuthStateChanging || isUserCheckInProgress || (!batchId && !systemUserBatchId)) ? (
          <PageLoadingSkeleton />
        ) : (
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            {/* Market Briefing Section */}
            <section className="pt-6 pb-8 border-b border-white/[0.03]">
              <MarketBriefingSection onPeriodChange={setActivePeriod} audioUrls={audioUrls} briefingGeneratedAt={batchRunInfo?.lastProcessedOn ?? batchRunInfo?.runEnd} />
            </section>

            {/* Headlines Section */}
            <section className="py-4 md:py-8 border-b border-white/[0.03]">
              <div className="flex items-center gap-2 mb-3 md:mb-4">
                <Zap className="w-5 h-5 text-amber-400" />
                <span className="text-sm font-medium text-amber-400 tracking-wide uppercase">
                  Top Stories
                </span>
              </div>
              <div className="group rounded-2xl border border-white/[0.06] overflow-hidden backdrop-blur-sm transition-all duration-300 hover:border-amber-500/20 hover:shadow-[0_0_24px_rgba(251,191,36,0.06)]">
                <div className="bg-gradient-to-br from-white/[0.02] to-transparent">
                  <HeadlineComponent onExpandChange={setHeadlineExpanded} />
                </div>
              </div>
            </section>

            {/* Sentiment Section */}
            <section className="py-8 border-b border-white/[0.03]">
              {/* Section Header */}
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-amber-400" />
                  <h2 className="text-xl font-bold text-white">
                    Sentiment
                  </h2>
                </div>

                <div className="flex items-center gap-3">
                  {/* Subscribe tag for non-logged-in users */}
                  {!userInfo && showSubscribeTag && (
                    <span className="px-3 py-1.5 bg-amber-500/10 text-amber-400 text-xs font-medium rounded-full border border-amber-500/20">
                      <span className="hidden sm:inline">Subscribe for personalized insights</span>
                      <span className="sm:hidden">Subscribe</span>
                    </span>
                  )}

                  {/* Toggle Buttons */}
                  <div className={`flex items-center bg-white/[0.02] border border-white/[0.06] rounded-xl p-1 ${!hasSentimentData ? 'opacity-50' : ''}`}>
                    <button
                      onClick={() => setSentimentMode('market')}
                      disabled={!hasSentimentData}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${sentimentMode === 'market'
                          ? 'bg-amber-500/15 text-amber-400 border border-amber-500/25 shadow-[0_0_12px_rgba(251,191,36,0.08)]'
                          : 'text-slate-400 hover:text-slate-300 hover:bg-white/[0.03]'
                        } ${!hasSentimentData ? 'cursor-not-allowed' : ''}`}
                    >
                      Market
                    </button>
                    <button
                      onClick={() => {
                        if (!hasSentimentData) return;
                        setShowSubscribeTag(false);
                        if (!userInfo) {
                          setShowSubscriptionStart(true);
                        } else {
                          setSentimentMode('personalized');
                        }
                      }}
                      disabled={!hasSentimentData}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${sentimentMode === 'personalized'
                          ? 'bg-amber-500/15 text-amber-400 border border-amber-500/25 shadow-[0_0_12px_rgba(251,191,36,0.08)]'
                          : 'text-slate-400 hover:text-slate-300 hover:bg-white/[0.03]'
                        } ${!hasSentimentData ? 'cursor-not-allowed' : ''}`}
                    >
                      {userInfo && lists.length > 0 ? 'Personalized' : 'Personalize'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Sentiment Content */}
              <div className="group rounded-2xl border border-white/[0.06] overflow-hidden backdrop-blur-sm transition-all duration-300 hover:border-amber-500/20 hover:shadow-[0_0_24px_rgba(251,191,36,0.06)]">
                <div className="bg-gradient-to-br from-white/[0.02] to-transparent">
                  {sentimentMode === 'market' ? (
                    <SentimentScoreDots userId={SYSTEM_USER_ID} batchId={systemUserBatchId} onDataAvailable={setHasSentimentData} />
                  ) : (
                    user && batchId ? (
                      lists.length > 0 ? (
                        <SentimentScoreDots userId={user.uid} batchId={batchId} onDataAvailable={setHasSentimentData} />
                      ) : (
                        // Prompt to create list
                        <div className="p-12">
                          <div className="flex flex-col items-center text-center">
                            <div className="w-16 h-16 mb-4 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center backdrop-blur-sm">
                              <List className="w-8 h-8 text-amber-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-white mb-2">Create Your Personalized List</h3>
                            <p className="text-slate-400 mb-6 max-w-md leading-relaxed">
                              Add your favorite stocks and tickers to get sentiment analysis tailored to your portfolio.
                            </p>
                            <a
                              href="/watchlist"
                              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold shadow-lg shadow-amber-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-amber-500/40 hover:scale-[1.02]"
                            >
                              Create Your List
                            </a>
                          </div>
                        </div>
                      )
                    ) : (
                      // Signup prompt
                      <div className="p-12">
                        <div className="flex flex-col items-center text-center">
                          <div className="w-16 h-16 mb-4 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center backdrop-blur-sm">
                            <TrendingUp className="w-8 h-8 text-amber-400" />
                          </div>
                          <h3 className="text-xl font-semibold text-white mb-2">Get Personalized Sentiment</h3>
                          <p className="text-slate-400 mb-6 max-w-md leading-relaxed">
                            Sign up to receive sentiment analysis tailored to your watchlist and investment interests.
                          </p>
                          <a
                            href="/signup"
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold shadow-lg shadow-amber-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-amber-500/40 hover:scale-[1.02]"
                          >
                            Sign Up Free
                          </a>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            </section>

            {/* Details Section */}
            {!headlineExpanded && (
              <section className="py-8 pb-24">
                {/* Section Header */}
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-amber-400" />
                    <h2 className="text-xl font-bold text-white">
                      Details
                    </h2>
                  </div>

                  {/* Toggle Buttons */}
                  <div className={`flex items-center bg-white/[0.02] border border-white/[0.06] rounded-xl p-1 ${!hasBulletinData ? 'opacity-50' : ''}`}>
                    <button
                      onClick={() => setShowListView(false)}
                      disabled={!hasBulletinData}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${!showListView
                          ? 'bg-amber-500/15 text-amber-400 border border-amber-500/25 shadow-[0_0_12px_rgba(251,191,36,0.08)]'
                          : 'text-slate-400 hover:text-slate-300 hover:bg-white/[0.03]'
                        } ${!hasBulletinData ? 'cursor-not-allowed' : ''}`}
                    >
                      Bulletin
                    </button>
                    <button
                      onClick={() => setShowListView(true)}
                      disabled={!hasBulletinData}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${showListView
                          ? 'bg-amber-500/15 text-amber-400 border border-amber-500/25 shadow-[0_0_12px_rgba(251,191,36,0.08)]'
                          : 'text-slate-400 hover:text-slate-300 hover:bg-white/[0.03]'
                        } ${!hasBulletinData ? 'cursor-not-allowed' : ''}`}
                    >
                      List
                    </button>
                  </div>
                </div>

                {/* Details Content */}
                <div className="group rounded-2xl border border-white/[0.06] overflow-hidden backdrop-blur-sm transition-all duration-300 hover:border-amber-500/20 hover:shadow-[0_0_24px_rgba(251,191,36,0.06)]">
                  <div className="bg-gradient-to-br from-white/[0.02] to-transparent">
                    {showListView ? (
                      <NewsLetterContentWrapper listIds={finalListIds} batchId={batchId} />
                    ) : (
                      <FinancialBulletin userId={user?.uid || ''} batchId={batchId} onDataAvailable={setHasBulletinData} />
                    )}
                  </div>
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <Footer
        onSubscribeClick={() => setShowSubscriptionStart(true)}
        showSubscribeButton={!showSubscriptionStart}
      />
    </div>
  );
}
